/**
 * TNK-134 phase B — Results page. Dedicated landing for a committed
 * import: celebratory header + Cards-from-this-import panel +
 * collapsible audit table of the staged CSV rows + Rollback action.
 *
 * Split off from /tools/imports/$id (which now handles only the
 * pre-commit review wizard) so the URL tells the story and Back
 * goes to the imports list instead of bouncing back into the wizard.
 *
 * If the operator navigates here for a draft that is NOT committed
 * (someone shared a stale link, or the page refetched while the
 * status was still flipping), we bounce them back to the wizard.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Undo2,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { FIGMA } from "@/components/import/figmaStyles";
import { ImportStepper } from "@/components/import/ImportStepper";
import {
  getImportCards,
  getImportDraft,
  rollbackImportDraft,
  RollbackBlockedError,
  type ApiBlockedCard,
  type ApiImportCard,
  type ApiImportDraft,
  type ApiImportDraftItem,
} from "@/features/importsApi";

export const Route = createFileRoute("/tools/imports/$id/results")({
  component: ImportResultsPage,
});

const ROLLBACK_TOKEN = "ROLLBACK";

interface ParsedShape {
  name?: string;
  setName?: string;
  cardNumber?: string;
  condition?: string;
  quantity?: number;
  marketPrice?: number;
}

function ImportResultsPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();

  const [draft, setDraft] = useState<ApiImportDraft | null>(null);
  const [items, setItems] = useState<ApiImportDraftItem[]>([]);
  const [draftError, setDraftError] = useState<string>("");

  const [importCards, setImportCards] = useState<ApiImportCard[] | null>(null);
  const [importCardsError, setImportCardsError] = useState<string>("");

  const [rollbackPhase, setRollbackPhase] = useState<"idle" | "confirm">(
    "idle",
  );
  const [rollbackTyped, setRollbackTyped] = useState("");
  const [rollbackInFlight, setRollbackInFlight] = useState(false);
  const [rollbackBlockers, setRollbackBlockers] = useState<ApiBlockedCard[]>(
    [],
  );
  const [rollbackMessage, setRollbackMessage] = useState<string>("");

  const [showAudit, setShowAudit] = useState(false);

  // Load the draft + items + cards in parallel. Bounce back to the
  // wizard if the draft is not committed — same URL contract as the
  // sales pick page.
  useEffect(() => {
    let cancelled = false;
    setDraftError("");
    setImportCardsError("");
    void Promise.all([getImportDraft(id), getImportCards(id)])
      .then(([draftState, cardsResp]) => {
        if (cancelled) return;
        if (draftState.draft.status !== "committed") {
          void navigate({
            to: "/tools/imports/$id",
            params: { id },
            replace: true,
          });
          return;
        }
        setDraft(draftState.draft);
        setItems(draftState.items);
        setImportCards(cardsResp.cards);
      })
      .catch((err) => {
        if (cancelled) return;
        setDraftError((err as Error).message ?? "Couldn't load the import.");
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const tokenMatch = rollbackTyped.trim().toUpperCase() === ROLLBACK_TOKEN;

  const handleRollback = async () => {
    setRollbackInFlight(true);
    setRollbackBlockers([]);
    setRollbackMessage("");
    try {
      const result = await rollbackImportDraft(id);
      setImportCards([]);
      setRollbackPhase("idle");
      setRollbackTyped("");
      // TNK-133 phase D — flip the local draft to the rolled-back view
      // without a refetch. The API returns the timestamp it stamped
      // when deleted > 0; when 0, we leave the draft untouched (no
      // server-side rolledBackAt was set).
      if (result.rolledBackAt) {
        setDraft((d) => (d ? { ...d, rolledBackAt: result.rolledBackAt } : d));
      }
      setRollbackMessage(
        result.deleted > 0
          ? `Rolled back — ${result.deleted} card${result.deleted === 1 ? "" : "s"} removed from inventory.`
          : "Nothing to roll back — the cards were already gone.",
      );
    } catch (err) {
      if (err instanceof RollbackBlockedError) {
        setRollbackBlockers(err.blockers);
        setRollbackMessage(err.message);
      } else {
        setRollbackMessage((err as Error).message ?? "Rollback failed.");
      }
    } finally {
      setRollbackInFlight(false);
    }
  };

  if (draftError) {
    return (
      <section className="flex h-full flex-col space-y-4">
        <BackButton fallback="/tools/imports" />
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{draftError}</span>
        </div>
      </section>
    );
  }

  if (!draft) {
    return (
      <section className="flex h-full flex-col items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
        <p className="mt-3 text-sm">Loading import…</p>
      </section>
    );
  }

  const total = importCards?.length ?? 0;

  return (
    <section className="flex h-full flex-col space-y-6 pb-12">
      <BackButton fallback="/tools/imports" />

      <ImportStepper currentStep="commit" importId={id} />

      {/* Header — celebratory on a healthy committed import, dim
          "Rolled back" on one that's been reversed. TNK-133 phase D
          distinguishes the two with `rolledBackAt`; the cards panel
          below shows 0 either way so the header is the only visual
          cue to the operator. */}
      {draft.rolledBackAt ? (
        <div
          className="rounded-xl p-6"
          style={{
            background: "rgba(74,85,104,0.08)",
            border: "1px solid rgba(74,85,104,0.22)",
          }}
        >
          <div className="flex items-start gap-3">
            <Undo2 size={28} style={{ color: FIGMA.muted }} />
            <div className="flex-1">
              <h1
                className="text-2xl"
                style={{
                  fontFamily: FIGMA.fontHeading,
                  fontWeight: 700,
                  color: FIGMA.muted,
                  letterSpacing: "0.02em",
                }}
              >
                Rolled back
              </h1>
              <p className="mt-1 text-sm" style={{ color: FIGMA.muted }}>
                This import was rolled back on{" "}
                <span style={{ fontFamily: FIGMA.fontMono }}>
                  {new Date(draft.rolledBackAt).toLocaleString()}
                </span>
                . The cards it produced have been removed from inventory.
              </p>
              <p
                className="mt-1 text-xs"
                style={{
                  fontFamily: FIGMA.fontMono,
                  color: FIGMA.veryDim,
                }}
              >
                {draft.id}
              </p>
            </div>
            <Link
              to="/tools/imports"
              className="rounded-xl border border-border px-4 py-2 text-foreground transition-colors hover:bg-muted"
              style={{
                fontFamily: FIGMA.fontBody,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Done
            </Link>
          </div>
        </div>
      ) : (
        <div
          className="rounded-xl p-6"
          style={{
            background: FIGMA.goodBg,
            border: `1px solid ${FIGMA.goodBorder}`,
          }}
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 size={28} style={{ color: FIGMA.good }} />
            <div className="flex-1">
              <h1
                className="text-2xl"
                style={{
                  fontFamily: FIGMA.fontHeading,
                  fontWeight: 700,
                  color: FIGMA.fg,
                  letterSpacing: "0.02em",
                }}
              >
                Import complete
              </h1>
              <p className="mt-1 text-sm" style={{ color: FIGMA.muted }}>
                <span style={{ fontFamily: FIGMA.fontMono }}>{total}</span> card
                {total === 1 ? "" : "s"} from{" "}
                {draft.sourceFilename ?? "this CSV"} landed in inventory.
              </p>
              <p
                className="mt-1 text-xs"
                style={{
                  fontFamily: FIGMA.fontMono,
                  color: FIGMA.veryDim,
                }}
              >
                {draft.id}
              </p>
            </div>
            <Link
              to="/tools/imports"
              className="rounded-xl px-4 py-2 transition-all"
              style={{
                background: FIGMA.indigoGradient,
                color: "#fff",
                fontFamily: FIGMA.fontBody,
                fontSize: 13,
                fontWeight: 600,
                boxShadow: FIGMA.indigoShadow,
              }}
            >
              Done
            </Link>
          </div>
        </div>
      )}

      <CommittedImportPanel
        draftId={draft.id}
        cards={importCards}
        cardsError={importCardsError}
        rollbackPhase={rollbackPhase}
        setRollbackPhase={(p) => {
          setRollbackPhase(p);
          setRollbackTyped("");
          setRollbackBlockers([]);
          setRollbackMessage("");
        }}
        rollbackTyped={rollbackTyped}
        setRollbackTyped={setRollbackTyped}
        rollbackInFlight={rollbackInFlight}
        rollbackBlockers={rollbackBlockers}
        rollbackMessage={rollbackMessage}
        tokenMatch={tokenMatch}
        onRollback={() => void handleRollback()}
      />

      {/* Audit table — collapsed by default so the operator who came
          here for a quick "what landed" doesn't have to scroll past
          400 rows. Open with one click. */}
      <div className="rounded-lg border border-border bg-card">
        <button
          type="button"
          onClick={() => setShowAudit((v) => !v)}
          className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-muted/30"
        >
          <div>
            <div className="text-sm font-semibold text-foreground">
              Original CSV rows · {items.length} row
              {items.length === 1 ? "" : "s"}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Audit trail of what was staged before commit.
            </div>
          </div>
          {showAudit ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {showAudit && (
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Set</th>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Cond</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item) => {
                  const p = (item.parsed ?? {}) as ParsedShape;
                  return (
                    <tr key={item.id} className="hover:bg-muted/20">
                      <td className="px-3 py-1.5 font-medium text-foreground">
                        {p.name || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {p.setName || "—"}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-muted-foreground">
                        {p.cardNumber || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {p.condition || "NM"}
                      </td>
                      <td className="px-3 py-1.5 text-right text-muted-foreground">
                        {p.quantity ?? 1}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-foreground">
                        ${(p.marketPrice ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function CommittedImportPanel({
  draftId,
  cards,
  cardsError,
  rollbackPhase,
  setRollbackPhase,
  rollbackTyped,
  setRollbackTyped,
  rollbackInFlight,
  rollbackBlockers,
  rollbackMessage,
  tokenMatch,
  onRollback,
}: {
  draftId: string;
  cards: ApiImportCard[] | null;
  cardsError: string;
  rollbackPhase: "idle" | "confirm";
  setRollbackPhase: (p: "idle" | "confirm") => void;
  rollbackTyped: string;
  setRollbackTyped: (s: string) => void;
  rollbackInFlight: boolean;
  rollbackBlockers: ApiBlockedCard[];
  rollbackMessage: string;
  tokenMatch: boolean;
  onRollback: () => void;
}) {
  const tableCap = 30;
  const total = cards?.length ?? 0;
  const visible = cards?.slice(0, tableCap) ?? [];

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Cards from this import
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {cards === null
              ? "Loading…"
              : total === 0
                ? "No cards from this import are in inventory."
                : `${total} card${total === 1 ? "" : "s"} stamped with ${draftId.slice(0, 12)}… — these came from this CSV.`}
          </p>
        </div>
        {total > 0 && (
          <Link
            to="/inventory"
            className="flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
          >
            <ExternalLink size={12} />
            Open in inventory
          </Link>
        )}
      </div>

      {cardsError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{cardsError}</span>
        </div>
      )}

      {rollbackMessage && (
        <div
          className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
            rollbackBlockers.length > 0
              ? "border-warning/40 bg-warning/10 text-warning"
              : "border-success/40 bg-success/10 text-success"
          }`}
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{rollbackMessage}</span>
        </div>
      )}

      {visible.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="min-w-full text-xs">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Set</th>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Cond.</th>
                <th className="px-3 py-2 font-medium">Qty</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-1.5 font-medium text-foreground">
                    {c.name || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {c.setName || "—"}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-muted-foreground">
                    {c.cardNumber || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {c.condition}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {c.quantity}
                  </td>
                  <td className="px-3 py-1.5">
                    <CardStatusBadge
                      listingStatus={c.listingStatus}
                      packagedProductId={c.packagedProductId}
                      archived={!!c.archivedAt}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > tableCap && (
            <div className="border-t border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Showing first {tableCap} of {total}. Open in inventory to see the
              rest.
            </div>
          )}
        </div>
      )}

      {rollbackBlockers.length > 0 && (
        <div className="space-y-1.5 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
          <p className="font-medium text-foreground">
            {rollbackBlockers.length} card
            {rollbackBlockers.length === 1 ? " is" : "s are"} still in use:
          </p>
          <ul className="ml-4 list-disc space-y-0.5 text-muted-foreground">
            {rollbackBlockers.slice(0, 10).map((b) => (
              <li key={b.cardId}>
                <span className="font-mono">{b.cardId.slice(0, 14)}…</span> —{" "}
                {b.reasons.join(", ")}
              </li>
            ))}
            {rollbackBlockers.length > 10 && (
              <li>…and {rollbackBlockers.length - 10} more.</li>
            )}
          </ul>
          <p className="text-muted-foreground">
            Free these (end the sale, restore from a product, or archive the
            reservation) before retrying.
          </p>
        </div>
      )}

      {total > 0 && rollbackPhase === "idle" && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">
            Made a mess? Roll back the entire import in one shot.
          </p>
          <button
            type="button"
            onClick={() => setRollbackPhase("confirm")}
            className="flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            <Undo2 size={12} />
            Roll back this import
          </button>
        </div>
      )}

      {rollbackPhase === "confirm" && (
        <div className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <p className="text-xs text-foreground">
            This deletes {total} card{total === 1 ? "" : "s"} from inventory.
            The CSV record stays as audit trail. To confirm, type{" "}
            <span className="font-mono font-bold">{ROLLBACK_TOKEN}</span> below.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={rollbackTyped}
              onChange={(e) => setRollbackTyped(e.target.value)}
              placeholder={ROLLBACK_TOKEN}
              className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => setRollbackPhase("idle")}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onRollback}
              disabled={!tokenMatch || rollbackInFlight}
              className="flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rollbackInFlight ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Undo2 size={12} />
              )}
              {rollbackInFlight ? "Rolling back…" : "Confirm rollback"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CardStatusBadge({
  listingStatus,
  packagedProductId,
  archived,
}: {
  listingStatus: string;
  packagedProductId: string | null;
  archived: boolean;
}) {
  if (archived)
    return (
      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        Archived
      </span>
    );
  if (packagedProductId)
    return (
      <span className="rounded bg-info/15 px-1.5 py-0.5 text-[10px] font-medium text-info">
        In product
      </span>
    );
  if (listingStatus !== "Unlisted")
    return (
      <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning">
        {listingStatus}
      </span>
    );
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      Unlisted
    </span>
  );
}
